import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, Chip, Dialog, DialogContent, Typography } from "@mui/material";

const ViewShiftsModal = ({
  open,
  onClose,
  employee,
  currentJob,
  dynamicShiftOptions,
  getAvailableDaysForShift,
  onSave,
}) => {
  const [currentShifts, setCurrentShifts] = useState({});
  const [shiftDays, setShiftDays] = useState({});

  // Helper to get available days with times for a shift
  const getAvailableDaysWithTimesForShift = useMemo(() => {
    return (shiftName) => {
      const shift = (currentJob?.shifts || []).find((s) => s.shiftName === shiftName);
      if (!shift || !shift.defaultSchedule) return [];
      return Object.entries(shift.defaultSchedule)
        .filter(([, sched]) => sched.start && sched.end)
        .map(([day, sched]) => ({
          day: day.substring(0, 3).toUpperCase(),
          start: sched.start,
          end: sched.end,
        }));
    };
  }, [currentJob?.shifts]);

  // Helper to format time to 12hr
  const formatTo12Hr = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours %= 12;
    hours = hours || 12;
    const minStr = `${minutes < 10 ? "0" : ""}${minutes}`;
    return `${hours}:${minStr} ${ampm}`;
  };

  useEffect(() => {
    if (!employee || !open) return;

    // Initialize shifts
    const initialShifts = dynamicShiftOptions.reduce((acc, shift) => {
      acc[shift.name] = employee.shifts.includes(shift.name);
      return acc;
    }, {});
    setCurrentShifts(initialShifts);

    // Initialize day selections for each shift
    const initialShiftDays = {};
    dynamicShiftOptions.forEach((shift) => {
      if (employee.shifts.includes(shift.name)) {
        // If staff already has this shift, initialize with existing data or default days
        initialShiftDays[shift.name] =
          employee.shiftDays?.[shift.name] || getAvailableDaysForShift(shift.name);
      } else if (initialShifts[shift.name]) {
        // If not assigned, default to all available days if selected
        initialShiftDays[shift.name] = getAvailableDaysForShift(shift.name);
      }
    });
    setShiftDays(initialShiftDays);
  }, [employee, open, dynamicShiftOptions, getAvailableDaysForShift]);

  const handleShiftSelectionChange = (shiftName) => {
    setCurrentShifts((prev) => {
      const checked = !prev[shiftName];
      return { ...prev, [shiftName]: checked };
    });
    setShiftDays((prev) => {
      const checked = !currentShifts[shiftName];
      if (checked) {
        // Select all available days
        return { ...prev, [shiftName]: getAvailableDaysForShift(shiftName) };
      }
      // Deselect all days
      const { [shiftName]: omit, ...rest } = prev;
      return rest;
    });
  };

  const handleDaySelection = (shiftName, day) => {
    setShiftDays((prev) => {
      const currentShiftDays = prev[shiftName] || [];
      const updatedDays = currentShiftDays.includes(day)
        ? currentShiftDays.filter((d) => d !== day)
        : [...currentShiftDays, day];

      return {
        ...prev,
        [shiftName]: updatedDays,
      };
    });
  };

  const isDaySelected = (shiftName, day) => {
    return (shiftDays[shiftName] || []).includes(day);
  };

  const handleSave = () => {
    onSave({ currentShifts, shiftDays });
  };

  if (!employee) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "400px",
          transition: "height 0.3s ease-in-out",
          overflow: "hidden",
          minWidth: "850px",
        },
      }}
    >
      <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
        <Box
          sx={{
            p: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, flexShrink: 0 }}>
            Select Shifts and Days
          </Typography>
          <Typography variant="body1" sx={{ color: "#666", mb: 3, flexShrink: 0 }}>
            For: {employee.firstName} {employee.lastName}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              maxHeight: "650px",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
                "&:hover": {
                  background: "#a8a8a8",
                },
              },
            }}
          >
            {dynamicShiftOptions.map((shift) => {
              const daysWithTimes = getAvailableDaysWithTimesForShift(shift.name);
              return (
                <Box key={shift.name} sx={{ minWidth: 280 }}>
                  <Box
                    onClick={() => handleShiftSelectionChange(shift.name)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      bgcolor: currentShifts[shift.name] ? "#f8f9fa" : "white",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "#f8f9fa",
                        transform: "translateY(-1px)",
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Checkbox
                      checked={currentShifts[shift.name] || false}
                      onChange={() => handleShiftSelectionChange(shift.name)}
                      sx={{
                        color: "#1976d2",
                        "&.Mui-checked": { color: "#1976d2" },
                        "& .MuiSvgIcon-root": { fontSize: 20 },
                        transition: "transform 0.2s ease",
                        "&:hover": { transform: "scale(1.1)" },
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box sx={{ flex: 1, ml: 1 }}>
                      <Typography
                        variant="body1"
                        fontWeight="600"
                        sx={{
                          color: currentShifts[shift.name] ? "#1976d2" : "#333",
                          fontSize: "14px",
                          transition: "color 0.3s ease",
                        }}
                      >
                        {shift.name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Expandable Day Selection */}
                  <Box
                    sx={{
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      maxHeight: currentShifts[shift.name] ? "200px" : "0px",
                      opacity: currentShifts[shift.name] ? 1 : 0,
                      transform: currentShifts[shift.name] ? "translateY(0)" : "translateY(-10px)",
                      mb: currentShifts[shift.name] ? 1 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#f8f9fa",
                        borderRadius: 2,
                        border: "1px solid #e3f2fd",
                        boxShadow: "0px 2px 8px rgba(25, 118, 210, 0.1)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        sx={{
                          color: "#1976d2",
                          mb: 1.5,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Select Days:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {daysWithTimes.map(({ day, start, end }) => (
                          <Chip
                            key={day}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  px: 1,
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: isDaySelected(shift.name, day) ? "white" : "#1976d2",
                                  }}
                                >
                                  {day}
                                </span>
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: isDaySelected(shift.name, day) ? "white" : "#1976d2",
                                    fontWeight: 500,
                                  }}
                                >
                                  {formatTo12Hr(start)} - {formatTo12Hr(end)}
                                </span>
                              </Box>
                            }
                            onClick={() => handleDaySelection(shift.name, day)}
                            sx={{
                              bgcolor: isDaySelected(shift.name, day)
                                ? "#1976d2"
                                : "rgb(235, 245, 253)",
                              color: isDaySelected(shift.name, day) ? "white" : "#1976d2",
                              border: "1px solid",
                              borderColor: isDaySelected(shift.name, day) ? "#1976d2" : "#90caf9",
                              fontSize: "12px",
                              fontWeight: 600,
                              height: "36px",
                              width: 135,
                              cursor: "pointer",
                              transition: "all 0.2s",
                              boxShadow: isDaySelected(shift.name, day)
                                ? "0px 2px 8px rgba(25, 118, 210, 0.15)"
                                : "none",
                              "&:hover": {
                                bgcolor: isDaySelected(shift.name, day) ? "#1565c0" : "#bbdefb",
                                color: "white",
                                borderColor: "#1565c0",
                                transform: "scale(1.05)",
                              },
                              m: 0.5,
                              px: 1.5,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexShrink: 0,
              pt: 2,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: "600",
                borderColor: "#ddd",
                color: "#666",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#bbb",
                  bgcolor: "#f8f9fa",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: "600",
                boxShadow: "0px 3px 12px rgba(25, 118, 210, 0.3)",
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewShiftsModal;
